import { createAction } from 'typesafe-actions';
import {
    CLEAR_SEARCH_RESULTS,
    GET_AZ_LIST_FAILURE,
    GET_AZ_LIST_REQUEST,
    GET_AZ_LIST_SUCCESS,
    GET_LIST_ITEM_PAGE_FAILURE,
    GET_LIST_ITEM_PAGE_REQUEST,
    GET_LIST_ITEM_PAGE_SUCCESS,
    GET_SEARCH_LIST_FAILURE,
    GET_SEARCH_LIST_REQUEST,
    GET_SEARCH_LIST_SUCCESS,
    LIST_TAKE_AMOUNT,
    SET_ACTIVE_SLUG,
} from './listConstants';
import { Dispatch } from 'redux';
import { client } from '../Common/gql/client';
import {
    activeCurriculumTypeSelector,
    curriculumTypesSelector,
    shouldRenderAZSelector,
} from '../AppConfig/appConfigSelectors';
import {
    filterStateSelector,
    activePageIndexSelector,
} from '../Filter/filterSelectors';
import { GetState, RootState } from '../Common/store/rootReducer';
import { activeCTSlugSelector } from './listSelectors';
import {
    GetContentRevisionsOpts,
    getListQueryName,
    listItemPageQuery,
    listQuery,
    combineDynamicFilters,
} from './listQuery';
import { resetActivePageAction } from '../Filter/filterActions';

export const setActiveSlug = createAction(SET_ACTIVE_SLUG)<string>();

export const setActiveSlugUsingIndex = (activeTabIndex: number) => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    const state = getState();
    const curriculumTypes = curriculumTypesSelector(state) || [];

    const activeCurriculumType = curriculumTypes[activeTabIndex];
    const slug = activeCurriculumType && activeCurriculumType.slug;

    dispatch(setActiveSlug(slug));
};

export const getAZListRequestAction = createAction(GET_AZ_LIST_REQUEST)();
export const getAZListSuccessAction = createAction(GET_AZ_LIST_SUCCESS)<{
    list: Array<unknown>;
    totalPages: number;
}>();
export const getAZListFailureAction = createAction(GET_AZ_LIST_FAILURE)<
    Error
>();

export const getAZList = () => async (
    dispatch: Dispatch,
    getState: GetState,
) => {
    dispatch(getAZListRequestAction());
    const state = getState();

    const {
        activeCurriculumType,
        filters: { skip, take, startsWith, dynamicFilters },
    } = getListOpts(state);

    const formattedDynamicFilters = activeCurriculumType
        ? combineDynamicFilters(dynamicFilters, activeCurriculumType)
        : {};

    const query = listQuery({
        activeCurriculumType,
        filters: {
            skip,
            take,
            startsWith,
            dynamicFilters: formattedDynamicFilters,
        },
    });

    try {
        const result_2 = await client.query({
            query,
        });
        const queryName = getListQueryName(activeCurriculumType);
        const list = result_2.data[queryName].results;
        const pageTotal = result_2.data[queryName].pageTotal;

        dispatch(
            getAZListSuccessAction({
                list,
                totalPages: pageTotal,
            }),
        );
    } catch (error) {
        return dispatch(getAZListFailureAction(error as Error));
    }
};

export const getSearchListRequestAction = createAction(GET_SEARCH_LIST_REQUEST)<
    string
>();
export const getSearchListSuccessAction = createAction(
    GET_SEARCH_LIST_SUCCESS,
)<{
    list: unknown[];
    totalPages: number;
}>();
export const getSearchListFailureAction = createAction(GET_SEARCH_LIST_FAILURE)<
    Error
>();

export const getSearchList = () => async (
    dispatch: Dispatch,
    getState: GetState,
) => {
    const state = getState();

    const {
        activeCurriculumType,
        filters: { skip, take, searchTerm = '', dynamicFilters },
    } = getListOpts(state);

    const formattedDynamicFilters = activeCurriculumType
        ? combineDynamicFilters(dynamicFilters, activeCurriculumType)
        : {};

    dispatch(getSearchListRequestAction(searchTerm));

    try {
        const result = await client.query({
            query: listQuery({
                activeCurriculumType,
                filters: {
                    skip,
                    take,
                    searchTerm,
                    dynamicFilters: formattedDynamicFilters,
                },
            }),
        });
        const queryName = getListQueryName(activeCurriculumType);
        const list = result.data[queryName].results;
        const pageTotal = result.data[queryName].pageTotal;

        dispatch(
            getSearchListSuccessAction({
                list,
                totalPages: pageTotal,
            }),
        );
    } catch (error) {
        return dispatch(getSearchListFailureAction(error as Error));
    }
};

export const clearSearchResultsAction = createAction(CLEAR_SEARCH_RESULTS)();

export const clearSearchResults = () => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    const state = getState();
    const activeCTSlug = activeCTSlugSelector(state);

    dispatch(clearSearchResultsAction());
    dispatch(resetActivePageAction({ activeCTSlug }));
};

const getListOpts = (state: RootState): GetContentRevisionsOpts => {
    const activeCurriculumType = activeCurriculumTypeSelector(state);
    const activePageIndex = activePageIndexSelector(state);
    const isAZ = shouldRenderAZSelector(state);

    if (activeCurriculumType === undefined) {
        throw new Error('Active curriculum type is undefined');
    }

    const filterState = filterStateSelector(state);
    const filters =
        filterState &&
        activeCurriculumType &&
        filterState[activeCurriculumType.slug]; // TODO replace with selector

    // eslint-disable-next-line
    const { startsWith, ...rest } = filters;

    const result: GetContentRevisionsOpts = {
        activeCurriculumType,
        filters: {
            skip: activePageIndex || 0,
            take: LIST_TAKE_AMOUNT,
            ...rest,
        },
    };

    // Only apply startsWith if it's configured to have the AZ component
    // and if there are no filters applied
    const isDynamicFiltersEmpty =
        Object.keys(filters.dynamicFilters).length === 0;
    if (isAZ && isDynamicFiltersEmpty) {
        result.filters.startsWith = filters.startsWith;
    }

    return result;
};

export const getListItemPageRequest = createAction(GET_LIST_ITEM_PAGE_REQUEST)<{
    uuid: string;
}>();
export const getListItemPageSuccess = createAction(GET_LIST_ITEM_PAGE_SUCCESS)<{
    //eslint-disable-next-line
    page: any;
    referrerUuid: string;
}>();
export const getListItemPageFailure = createAction(GET_LIST_ITEM_PAGE_FAILURE)<{
    error: Error;
    referrerUuid: string;
}>();

export const getListItemPage = (uuid: string) => async (dispatch: Dispatch) => {
    dispatch(getListItemPageRequest({ uuid }));

    try {
        const result = await client.query({
            query: listItemPageQuery(uuid),
        });
        const pageData = result.data && result.data.pageByContentRevision;
        dispatch(
            getListItemPageSuccess({
                page: pageData,
                referrerUuid: uuid,
            }),
        );
    } catch (error) {
        dispatch(
            getListItemPageFailure({
                error: error as Error,
                referrerUuid: uuid,
            }),
        );
    }
};
